import { ISSUE_REPORT_MAX_IMAGE_SIZE } from '../common-types/issue-report';
import { redactDocument } from './redact';

const PNG_TYPE = 'image/png';
const JPEG_TYPE = 'image/jpeg';
const JPEG_QUALITIES = [0.8, 0.6, 0.4];

// Canvas ceilings across browsers: the tightest dimension limit in common use,
// and iOS Safari's total-area limit, which is the tightest of all. A capture
// over either one comes back blank rather than failing, so clamp before asking.
const MAX_CANVAS_DIMENSION = 8192;
const MAX_CANVAS_AREA = 16777216;

export const PAGE_SCREENSHOT_PNG = 'page-screenshot.png';
export const PAGE_SCREENSHOT_JPG = 'page-screenshot.jpg';

const toBlob = (canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

// Some routes leave `body` as a viewport-height overflow box (`height: 100vh;
// overflow-y: auto` - the condo pages do). On the live page that is harmless:
// CSS propagates the root element's overflow to the viewport and body's *used*
// overflow becomes `visible`, so nothing clips. html2canvas does not implement
// that propagation - it reads the computed style, sees a one-viewport-tall
// overflow box, and clips the render to it, producing a single screenful of
// content followed by blank space (TRAV23-4765). Undo it on the clone only; the
// live page the member is looking at is never touched.
const unclipRoots = (clonedDocument: Document): void => {
  [clonedDocument.documentElement, clonedDocument.body].forEach((element) => {
    if (!element) {
      return;
    }

    element.style.height = 'auto';
    element.style.maxHeight = 'none';
    element.style.overflow = 'visible';
  });
};

const captureScale = (width: number, height: number): number => {
  if (width <= 0 || height <= 0) {
    return 1;
  }

  return Math.min(
    1,
    MAX_CANVAS_DIMENSION / width,
    MAX_CANVAS_DIMENSION / height,
    Math.sqrt(MAX_CANVAS_AREA / (width * height)),
  );
};

// Captures the whole page.
//
// This deliberately does NOT crop to the viewport. html2canvas rasterises a
// static copy of the document inside an iframe, where scripts never run and
// images reload from scratch, so the clone's layout does not always match the
// live page's - an <img> with no intrinsic dimensions is zero-height there
// until it loads. Cropping with `x`/`y`/`width`/`height` means mixing live-page
// coordinates with clone-space content, and when the two disagree the crop
// lands on empty space and the member gets a blank screenshot (TRAV23-4765).
//
// Capturing the full document removes that entire class of failure: with
// `scrollX`/`scrollY` pinned to 0 every coordinate html2canvas uses comes from
// the clone itself, including the canvas size, so there is nothing left to
// mis-align. The trade-off is a slower, larger capture on long pages, handled
// by scaling down to the canvas limits and stepping the JPEG quality down until
// it fits the server's size cap.
//
// This module is deliberately not re-exported from the `@share/utils` barrel:
// that barrel is pulled in by `axios.ts` during store bootstrap, and html2canvas
// has no business in that graph. html2canvas itself is imported lazily so it is
// only downloaded when a member actually opens the report modal.
export const capturePageScreenshot = async (): Promise<File | null> => {
  try {
    const { default: html2canvas } = await import('html2canvas');

    const documentWidth = document.documentElement.scrollWidth;
    const documentHeight = document.documentElement.scrollHeight;

    const canvas = await html2canvas(document.body, {
      logging: false,
      // Cross-origin images (hotel photos, maps) taint the canvas and make
      // toBlob throw, so ask for CORS and never allow a tainted canvas.
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      // Mask card, password and personal fields in the cloned DOM so they are
      // never rasterised. The live page the member sees is untouched.
      onclone: (clonedDocument: Document) => {
        redactDocument(clonedDocument);
        unclipRoots(clonedDocument);
      },
      scale: captureScale(documentWidth, documentHeight),
      // In html2canvas 1.x these are the scroll position applied to the clone,
      // not a translation offset - that was the 0.x meaning. Pinning them to 0
      // leaves every element in plain document coordinates; passing the live
      // scroll (or, as this once did, its negation) is what produced blank and
      // wrong-region captures.
      scrollX: 0,
      scrollY: 0,
    });

    const png = await toBlob(canvas, PNG_TYPE);

    if (png && png.size <= ISSUE_REPORT_MAX_IMAGE_SIZE) {
      return new File([png], PAGE_SCREENSHOT_PNG, { type: PNG_TYPE });
    }

    // A full-page PNG will usually be past the 5 MB server limit, so fall back
    // to JPEG and give up quality before giving up the screenshot entirely.
    // eslint-disable-next-line no-restricted-syntax
    for (const quality of JPEG_QUALITIES) {
      // eslint-disable-next-line no-await-in-loop
      const jpeg = await toBlob(canvas, JPEG_TYPE, quality);

      if (jpeg && jpeg.size <= ISSUE_REPORT_MAX_IMAGE_SIZE) {
        return new File([jpeg], PAGE_SCREENSHOT_JPG, { type: JPEG_TYPE });
      }
    }

    return null;
  } catch (e) {
    // A failed capture must never block the report itself.
    console.error('Page screenshot capture failed', e);
    return null;
  }
};
